import React, { useMemo, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Field, Fields } from 'redux-form'
import { useTranslation } from 'react-i18next'

import { AddressAutocompleteV2 } from './address-autocomplete-v2.component'
import {
  StyledAddressWrapper,
  StyledGenericAddressField,
  StyledNameAutocompleteV2,
  StyledPhoneNumberAutocomplete,
} from './address-form.component.styles'
import { HKTelephoneField, requiredField } from 'ggx-service/validation'
import { AddressBookModal } from 'ggx-src/address-book/address-book-modal/address-book-modal.component'
import useHoveringWaypointMenu from 'ggx-src/global/hooks/use-hovering-waypoint-menu.js'

const AddressForm = ({
  isAddressBookOptionsReady,
  handleResetAddressSyncErrors,
  section,
  onWaypointChange,
  onClickAddAddress,
}) => {
  const [t] = useTranslation()
  const requiredAddressDetailValidator = useMemo(
    () => requiredField(t('error.missing.floorRoom')),
    [t]
  )
  const HKTelephoneValidator = useMemo(
    () => HKTelephoneField(t('error.invalid.contact.number')),
    [t]
  )

  const addressDetailValidator = requiredAddressDetailValidator

  const [isShowAddressBookModal, setIsShowAddressBookModal] = useState(false)

  const waypointRef = useRef()
  const hoverMenuItem = useHoveringWaypointMenu(() => ({
    contact_name: waypointRef.current.querySelector(
      `[placeholder="${t('orders.new.basicInfo.placeholder.name')}"]`
    ),
    contact_phone_number: waypointRef.current.querySelector(
      `[placeholder="${t('orders.new.basicInfo.placeholder.contactNumber')}"]`
    ),
    address: waypointRef.current.querySelector(
      `[placeholder="${t('orders.new.basicInfo.placeholder.address1')}"]`
    ),
    detail_address: waypointRef.current.querySelector(
      `[placeholder="${t('orders.new.basicInfo.placeholder.address2')}"]`
    ),
  }))

  return (
    <div ref={waypointRef}>
      <StyledAddressWrapper>
        <Fields
          names={[
            'name',
            'phone_number',
            'address',
            'latitude',
            'longitude',
            'details',
          ]}
          dataTestid="delivery-order-name-autocomplete"
          placeholder={t('orders.new.basicInfo.placeholder.name')}
          component={StyledNameAutocompleteV2}
          props={{
            isAddressBookOptionsReady,
            onChange: value => {
              hoverMenuItem()
              onWaypointChange(value)
            },
            onInputChange: () => hoverMenuItem(),
            onBlur: () => hoverMenuItem(),
            onHover: hoverMenuItem,
            onOpenAddressBookModal: reason => {
              if (reason === 'pick-from-address-book')
                setIsShowAddressBookModal(true)
              else if (reason === 'save-address-book') onClickAddAddress()
            },
          }}
        />

        <Fields
          names={['phone_number', 'address', 'latitude', 'longitude']}
          placeholder={t('orders.new.basicInfo.placeholder.contactNumber')}
          component={StyledPhoneNumberAutocomplete}
          props={{
            isAddressBookOptionsReady,
            onChange: value => {
              hoverMenuItem()
              onWaypointChange(value)
            },
            onInputChange: () => hoverMenuItem(),
            onBlur: () => hoverMenuItem(),
            onHover: hoverMenuItem,
            onOpenAddressBookModal: reason => {
              if (reason === 'pick-from-address-book')
                setIsShowAddressBookModal(true)
              else if (reason === 'save-address-book') onClickAddAddress()
            },
          }}
          validate={HKTelephoneValidator}
        />
      </StyledAddressWrapper>

      <StyledAddressWrapper>
        <Fields
          names={['address', 'latitude', 'longitude']}
          dataTestid="delivery-order-address-autocomplete"
          placeholder={t('orders.new.basicInfo.placeholder.address1')}
          component={AddressAutocompleteV2}
          handleResetAddressSyncErrors={handleResetAddressSyncErrors}
          section={section}
          props={{
            isAddressBookOptionsReady,
            onChange: value => {
              hoverMenuItem()
              onWaypointChange(value)
            },
            onInputChange: () => hoverMenuItem(),
            onBlur: () => hoverMenuItem(),
            onHover: hoverMenuItem,
            onOpenAddressBookModal: reason => {
              if (reason === 'pick-from-address-book')
                setIsShowAddressBookModal(true)
              else if (reason === 'save-address-book') onClickAddAddress()
            },
          }}
        />

        <Field
          name="details"
          placeholder={t('orders.new.basicInfo.placeholder.address2')}
          component={StyledGenericAddressField}
          type="text"
          gridRatio="1/3"
          validate={addressDetailValidator}
          autocomplete="off"
        />
      </StyledAddressWrapper>
      {isShowAddressBookModal && (
        <AddressBookModal
          onSelect={waypoint => {
            hoverMenuItem()
            onWaypointChange({
              address: waypoint.address,
              latitude: waypoint.lat.toString(),
              longitude: waypoint.lon.toString(),
              details: waypoint.detail_address,
              phone_number: waypoint.contact_phone_number,
              name: waypoint.contact_name,
              saved_address_id: waypoint.id,
            })
            setIsShowAddressBookModal(false)
          }}
          onClose={() => setIsShowAddressBookModal(false)}
        />
      )}
    </div>
  )
}

AddressForm.propTypes = {
  isAddressBookOptionsReady: PropTypes.bool.isRequired,
  handleResetAddressSyncErrors: PropTypes.func.isRequired,
  section: PropTypes.string.isRequired,
  onWaypointChange: PropTypes.func.isRequired,
  onClickAddAddress: PropTypes.func.isRequired,
}

export { AddressForm }
